<template>
  <div class="dashboard-layout">
    <Toast ref="toastRef" />

    <aside class="sidebar">
      <div class="top-icons">
        <button class="grid-btn" type="button">
          <span v-for="n in 9" :key="n"></span>
        </button>
        <button class="icon-btn active" type="button">◫</button>
      </div>
    </aside>

    <div class="main-area">
      <header class="app-header">
        <h2>Omnichannel</h2>
      </header>

      <div class="page-body">
        <div class="page-head">
          <div>
            <h1>WhatsApp Configuration</h1>
            <p>Manage your WhatsApp channel</p>
          </div>

          <div class="toolbar">
            <button
              class="secondary-btn"
              type="button"
              @click="testConnection"
              :disabled="loading"
            >
              {{ loading ? "Testing..." : "Test" }}
            </button>

            <button
              class="primary-btn"
              type="button"
              @click="saveConfig"
              :disabled="loading"
            >
              {{ loading ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>

        <div class="cards-grid">
          <div class="card">
            <h3>Credentials</h3>

            <input
              v-model.trim="form.channel_name"
              type="text"
              placeholder="Channel Name"
            />

            <input
              v-model.trim="form.phone_number"
              type="text"
              placeholder="Phone Number"
            />

            <input
              v-model.trim="form.phone_number_id"
              type="text"
              placeholder="Phone Number ID"
            />

            <input
              v-model.trim="form.business_account_id"
              type="text"
              placeholder="Business Account ID"
            />

            <div class="password-wrap">
              <input
                v-model="form.access_token"
                :type="showToken ? 'text' : 'password'"
                placeholder="Access Token"
              />
              <button
                class="toggle-btn"
                type="button"
                @click="showToken = !showToken"
              >
                {{ showToken ? "Hide" : "Show" }}
              </button>
            </div>

            <div class="password-wrap">
              <input
                v-model="form.verify_token"
                :type="showVerifyToken ? 'text' : 'password'"
                placeholder="Verify Token"
              />
              <button
                class="toggle-btn"
                type="button"
                @click="showVerifyToken = !showVerifyToken"
              >
                {{ showVerifyToken ? "Hide" : "Show" }}
              </button>
            </div>

            <div class="password-wrap">
              <input
                v-model="form.app_secret"
                :type="showSecret ? 'text' : 'password'"
                placeholder="App Secret"
              />
              <button
                class="toggle-btn"
                type="button"
                @click="showSecret = !showSecret"
              >
                {{ showSecret ? "Hide" : "Show" }}
              </button>
            </div>
          </div>

          <div class="card">
            <h3>Webhook</h3>

            <input
              v-model.trim="form.webhook_url"
              type="text"
              placeholder="Webhook URL"
            />

            <input
              v-model.trim="form.api_version"
              type="text"
              placeholder="Version"
            />

            <textarea
              v-model="form.notes"
              rows="6"
              placeholder="Notes"
            ></textarea>

            <label class="checkbox-row">
              <span>Active</span>
              <input type="checkbox" v-model="form.is_active" />
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/services/auth";
import VARS from "@/services/vars";
import Toast from "@/components/AppToast.vue";

const router = useRouter();
const API = VARS.API_URL;

const toastRef = ref(null);
const loading = ref(false);

const showToken = ref(false);
const showVerifyToken = ref(false);
const showSecret = ref(false);

const form = ref({
  channel_name: "",
  phone_number: "",
  phone_number_id: "",
  business_account_id: "",
  access_token: "",
  verify_token: "",
  webhook_url: "",
  api_version: "v21.0",
  app_secret: "",
  notes: "",
  is_active: true,
});

async function apiGet(path) {
  const r = await fetch(API + path, {
    headers: {
      Authorization: "Bearer " + auth.token,
    },
  });

  if (r.status === 401) {
    router.push("/");
    throw new Error("UNAUTHORIZED");
  }

  const data = await r.json();
  if (!r.ok || !data.ok) {
    throw new Error(data?.message || "API_ERROR");
  }

  return data;
}

async function apiPost(path, body) {
  const r = await fetch(API + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify(body || {}),
  });

  if (r.status === 401) {
    router.push("/");
    throw new Error("UNAUTHORIZED");
  }

  const data = await r.json();
  if (!r.ok || !data.ok) {
    throw new Error(data?.message || "API_ERROR");
  }

  return data;
}

function notify({ title = "", message = "", type = "info" }) {
  toastRef.value?.showToast({
    title,
    message,
    type,
    duration: 3000,
  });
}

async function loadConfig() {
  try {
    loading.value = true;

    const data = await apiGet("/api/manager/whatsapp/config");

    if (data.config) {
      form.value = {
        ...form.value,
        ...data.config,
        is_active: Boolean(data.config.is_active),
      };
    }

    notify({
      title: "Loaded",
      message: "Config loaded successfully",
      type: "success",
    });
  } catch (e) {
    notify({
      title: "Error",
      message: e.message || "Failed to load config",
      type: "error",
    });
  } finally {
    loading.value = false;
  }
}

async function saveConfig() {
  try {
    loading.value = true;

    await apiPost("/api/manager/whatsapp/config", {
      ...form.value,
      is_active: form.value.is_active ? 1 : 0,
    });

    notify({
      title: "Saved",
      message: "Configuration saved successfully",
      type: "success",
    });
  } catch (e) {
    notify({
      title: "Error",
      message: e.message || "Save failed",
      type: "error",
    });
  } finally {
    loading.value = false;
  }
}

async function testConnection() {
  try {
    loading.value = true;

    const r = await apiPost("/api/manager/whatsapp/test", {
      phone_number_id: form.value.phone_number_id,
      access_token: form.value.access_token,
    });

    notify({
      title: "Success",
      message: r.message || "Connection OK",
      type: "success",
    });
  } catch (e) {
    notify({
      title: "Error",
      message: e.message || "Connection failed",
      type: "error",
    });
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  if (!auth.token) {
    router.push("/");
    return;
  }
  await loadConfig();
});
</script>

<style scoped>
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: #f6f8fb;
}

.sidebar {
  width: 60px;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-start;
  padding-top: 16px;
}

.top-icons {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.grid-btn {
  width: 34px;
  height: 34px;
  display: grid;
  grid-template-columns: repeat(3, 6px);
  gap: 3px;
  place-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 10px;
}

.grid-btn span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2f80ed;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
}

.icon-btn.active {
  background: #eaf2ff;
  color: #2f80ed;
}

.main-area {
  flex: 1;
  min-width: 0;
}

.app-header {
  height: 64px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

.app-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.page-body {
  padding: 20px;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.page-head h1 {
  margin: 0 0 6px;
  font-size: 24px;
}

.page-head p {
  margin: 0;
  color: #64748b;
}

.toolbar {
  display: flex;
  gap: 10px;
}

.primary-btn,
.secondary-btn,
.toggle-btn {
  border: none;
  cursor: pointer;
  border-radius: 10px;
  font-weight: 600;
}

.primary-btn {
  background: #2f80ed;
  color: #fff;
  padding: 10px 16px;
}

.secondary-btn {
  background: #fff;
  color: #183b67;
  border: 1px solid #cfd8e3;
  padding: 10px 16px;
}

.primary-btn:disabled,
.secondary-btn:disabled,
.toggle-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cards-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.card {
  background: #fff;
  padding: 20px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
}

.card h3 {
  margin: 0 0 16px;
  font-size: 18px;
}

input,
textarea {
  width: 100%;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #d1d9e6;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}

textarea {
  resize: vertical;
  min-height: 120px;
}

.password-wrap {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.password-wrap input {
  margin-bottom: 0;
  flex: 1;
}

.toggle-btn {
  background: #eef4ff;
  color: #1d4ed8;
  padding: 0 14px;
  white-space: nowrap;
}

.checkbox-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
}

.checkbox-row input {
  width: auto;
  margin: 0;
}

@media (max-width: 900px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }

  .page-head {
    flex-direction: column;
  }
}
</style>XW